import demandImg from '../../images/performanceai/demand.png';
import salesImg from '../../images/performanceai/sales.png';
import inventoryImg from '../../images/performanceai/inventory.png';
import logisticImg from '../../images/performanceai/logistic.png';
import receivablesImg from '../../images/performanceai/receivables.png';
import performanceEmailImg from '../../images/performanceai/performanceemail.png';

export const ASSUMPTIONS = {
    headers: ['Assumption', 'Value', 'Notes'],
    rows: [
        ['Target Fill Rate', '0.95', 'Desired service level for must-stock SKUs'],
        ['Critical Fill Rate', '0.85', 'Below this, service recovery action is triggered'],
        ['Critical Fill Drop', '-0.10', 'Recent fill-rate minus baseline below this triggers root cause review'],
        ['Critical Frequency Drop', '-0.10', 'Recent monthly order frequency minus baseline below this triggers review'],
        ['High Cover Days', '45', 'Above this suggests overstock / loading risk'],
        ['Low Cover Days', '7', 'Below this suggests depletion / stock-out risk'],
        ['Must-Stock Loss Factor', '0.80', 'Portion of short supply likely to become lost sales'],
        ['Long-Tail Loss Factor', '0.35', 'Portion of short supply likely to become lost sales'],
        ['Recent Window Days Default', '90', 'Replace with actual recent period days if known'],
        ['Escalation Volume Gap', '-100', 'Large negative order-size change that triggers action'],
        ['Review Cadence Days', '7', 'Suggested replenishment review cycle for recovery'],
        ['Root Cause Note', 'Thresholds should be calibrated by category/channel', 'Use market-specific norms where available'],
    ],
};

export const DATA_MODEL = {
    headers: ['Channel', 'SKU', 'SKU Type', 'Baseline Days', 'Recent Days', 'Baseline Orders', 'Baseline Ordered Qty', 'Baseline Filled Qty', 'Recent Orders', 'Recent Ordered Qty', 'Recent Filled Qty', 'Opening Stock', 'Receipts', 'Closing Stock', 'Non-Sale Outflows', 'Non-Sale Inflows', 'Service Notes'],
    rows: [
        ['Channel 1', 'SKU 1', 'Must-Stock', '456', '90', '5', '6,000', '4,500', '1', '1,000', '500', '900', '600', '700', '0', '0', 'Sample row from current analysis'],
        ['Channel 2', 'SKU 2', 'Must-Stock', '360', '90', '12', '3,000', '2,850', '1', '400', '200', '200', '150', '20', '0', '0', 'Dummy row — stock-out pattern'],
        ['Channel 1', 'SKU 3', 'Long-Tail', '300', '90', '10', '1,500', '1,425', '1', '150', '145', '300', '100', '220', '0', '0', 'Dummy row — overstock pattern'],
        ['Channel 3', 'SKU 1', 'Must-Stock', '270', '90', '9', '4,500', '4,350', '3', '750', '375', '400', '300', '350', '0', '0', 'Dummy row — retailer pullback pattern'],
        ['Channel 2', 'SKU 4', 'Long-Tail', '300', '90', '10', '1,000', '960', '3', '300', '288', '100', '80', '45', '0', '0', 'Dummy row — healthy / no action'],
    ],
};

export const INSIGHTS = {
    headers: ['Channel', 'SKU', 'SKU Type', 'Baseline Monthly Order Frequency', 'Recent Monthly Order Frequency', 'Order Frequency Change', 'Baseline Order Size', 'Recent Order Size', 'Order Size Change', 'Baseline Fill Rate', 'Recent Fill Rate', 'Fill Rate Change', 'Estimated Secondary Sales', 'Recent Avg Daily Secondary', 'Days of Cover', 'Short Supply Recent', 'Loss Factor', 'Potential Lost Sales', 'Primary Risk Flag', 'Root Cause Hypothesis', 'Immediate Channel Recovery', 'Immediate Supply Chain Recovery', 'Recovery Priority'],
    rows: [
        ['Channel 1', 'SKU 1', 'Must-Stock', '0.33/mo', '0.33/mo', '~0%', '1,200', '1,000', '-200 (-17%)', '75%', '50%', '-25pp', '800 units', '8.9/day', '~79 days', '500 units', '0.80', '400 units', 'High – fill breach + overstock', 'Fill rate down, order frequency stable → supply cuts without visible disengagement', 'Confirm outlet availability; reassure top retailers on ETA', 'Expedite dispatch to Channel 1; review allocation logic', 'High'],
        ['Channel 2', 'SKU 2', 'Must-Stock', '1.00/mo', '0.33/mo', '-0.67 (-67%)', '250', '400', '+150 (+60%)', '95%', '50%', '-45pp', '330 units', '3.7/day', '~6 days', '200 units', '0.80', '160 units', 'High – stock-out signal', 'Fill down + frequency down + cover low → stock-outs, weak replenishment', 'Prioritise depot allocation; check lead-time misses', 'Escalate dispatch cuts; validate top-SKU stock position', 'High'],
        ['Channel 1', 'SKU 3', 'Long-Tail', '1.00/mo', '0.33/mo', '-0.67 (-67%)', '150', '150', '0 (0%)', '95%', '97%', '+2pp', '180 units', '2.0/day', '~110 days', '5 units', '0.35', '2 units', 'Medium – overstock building', 'Order frequency down + cover high → overstock, slow liquidation, cash blockage', 'Review outlet pull; plan liquidation', 'Slow receipts on SKU 3; reassess assortment', 'Medium'],
        ['Channel 3', 'SKU 1', 'Must-Stock', '1.00/mo', '1.00/mo', '~0%', '500', '250', '-250 (-50%)', '97%', '50%', '-47pp', '350 units', '3.9/day', '~90 days', '375 units', '0.80', '300 units', 'High – retailer pullback', 'Order size down + fill down on must-stock SKU → retailer confidence loss', 'Sales rep visit; confirm availability in top outlets', 'Fix repeat stock non-availability on SKU 1', 'High'],
        ['Channel 2', 'SKU 4', 'Long-Tail', '1.00/mo', '1.00/mo', '~0%', '100', '100', '0 (0%)', '96%', '96%', '~0pp', '135 units', '1.5/day', '~30 days', '12 units', '0.35', '4 units', 'Low – healthy, no action', 'No thresholds breached — stable demand and service', '—', '—', 'Low'],
    ],
};

export const RCA_GUIDE = {
    headers: ['Trigger Pattern', 'Likely Root Cause Areas', 'What to Validate Immediately'],
    rows: [
        ['Fill rate down + order frequency down + cover low', 'Stock-outs, weak replenishment, wrong SKU mix, service instability', 'Dropped lines, lead time misses, depot availability, top-SKU stock position'],
        ['Fill rate down + order frequency stable', 'Supply cuts without visible channel disengagement yet', 'Production constraints, allocation logic, dispatch cuts by SKU'],
        ['Order frequency down + cover high', 'Overstock, slow liquidation, weak outlet pull, cash blockage', 'Aging stock, outlet productivity, secondary estimate trend, inventory by SKU'],
        ['Order size down + fill down on must-stock SKU', 'Retailer confidence loss or repeated stock non-availability', 'Availability in top outlets, repeat lost lines, sales rep feedback'],
        ['Secondary estimate down + cover rising', 'Pipeline loading or weakening demand', 'Receipts discipline, assortment rationalization, liquidation actions'],
    ],
};

export const SUMMARY_CARDS = [
    ['Rows assessed', '5'],
    ['High priority rows', '3'],
    ['Medium priority rows', '1'],
    ['Low priority rows', '1'],
    ['Total estimated secondary sales', '1,795 units'],
    ['Total potential lost sales', '866 units'],
    ['Average recent fill rate', '69%'],
    ['Average days of cover', '~63 days'],
];

export const ISSUE_KANBAN = [
    {
        title: 'Open',
        cards: [
            { priority: 'P0', title: 'Channel 1 · SKU 1', meta: 'Sample issue from current signal\nOwner: Sales + Supply\nDue in 7 days' },
            { priority: 'P0', title: 'Channel 2 · SKU 2', meta: 'Stock-out pattern — fill & frequency both down\nOwner: Supply Chain\nDue in 3 days' },
        ],
    },
    {
        title: 'In Progress',
        cards: [
            { priority: 'P1', title: 'Channel 3 · SKU 1', meta: 'Retailer order size collapsed — confidence risk\nOwner: Sales\nDue in 5 days' },
        ],
    },
    {
        title: 'Resolved',
        cards: [
            { priority: 'P2', title: 'Channel 1 · SKU 3', meta: 'Overstock building — liquidation plan actioned\nOwner: Supply Chain\nClosed 2 days ago', resolved: true },
        ],
    },
];

export const OPTIMIZATION_TAB_IMAGES = {
    performanceEmail: {
        imageSrc: performanceEmailImg,
        alt: 'Performance Email · Account Owner dealer-wise Excel attachments',
    },
};

export const IMAGE_SCREENS = {
    demand: {
        imageSrc: demandImg,
        alt: 'Demand Sense dashboard',
    },
    sales: {
        imageSrc: salesImg,
        alt: 'Trade Sales Target Tracker',
    },
    inventory: {
        imageSrc: inventoryImg,
        alt: 'Inventory Optimization dashboard',
    },
    logistics: {
        imageSrc: logisticImg,
        alt: 'Logistics dashboard',
    },
    receivables: {
        imageSrc: receivablesImg,
        alt: 'Receivables Ageing dashboard',
    },
};

export const MODULE_ROUTES = {
    optimization: '/dashboard/performance-ai/optimization',
    demand: '/dashboard/performance-ai/demand',
    sales: '/dashboard/performance-ai/sales',
    inventory: '/dashboard/performance-ai/inventory',
    logistics: '/dashboard/performance-ai/logistics',
    receivables: '/dashboard/performance-ai/receivables',
};

export const HUB_TILES = [
    { key: 'optimization', label: 'Optimization Model', link: MODULE_ROUTES.optimization, icon: '◎', sub: 'Channel Recovery' },
    { key: 'demand', label: 'Demand', link: MODULE_ROUTES.demand, icon: '▤', sub: 'Demand Sense' },
    { key: 'sales', label: 'Sales', link: MODULE_ROUTES.sales, icon: '⚑', sub: 'Sales & Optimization' },
    { key: 'inventory', label: 'Inventory Optimization', link: MODULE_ROUTES.inventory, icon: '▣', sub: 'Relaxo' },
    { key: 'logistics', label: 'Logistics', link: MODULE_ROUTES.logistics, icon: '➤', sub: 'Dispatch SLA / TAT' },
    { key: 'receivables', label: 'Receivables', link: MODULE_ROUTES.receivables, icon: '☰', sub: 'Ageing Analysis' },
];

export const getModuleFromPath = (pathname) => {
    const normalizedPath = pathname.replace(/\/$/, '');

    if (normalizedPath === '/dashboard/performance-ai') {
        return 'hub';
    }
    if (normalizedPath.includes('/enterprise-optimization')) return 'enterprise-optimization';
    if (normalizedPath.includes('/optimization')) return 'optimization';
    if (normalizedPath.includes('/demand')) return 'demand';
    if (normalizedPath.includes('/sales')) return 'sales';
    if (normalizedPath.includes('/inventory')) return 'inventory';
    if (normalizedPath.includes('/logistics')) return 'logistics';
    if (normalizedPath.includes('/receivables')) return 'receivables';
    return 'hub';
};
